import { useQuery } from '@tanstack/react-query';
import api from '../services/axiosInstance';

export interface PermissionsConfigModule {
  key: string;
  name: string;
  description: string;
  actions: string[];
}

export interface PermissionsConfigAction {
  key: string;
  name: string;
  description: string;
}

export interface PermissionsConfig {
  modules: PermissionsConfigModule[];
  actionCatalog: PermissionsConfigAction[];
}

const DEFAULT_ACTION_DESCRIPTIONS: Record<string, string> = {
  read: 'View records',
  create: 'Add new records',
  update: 'Edit existing records',
  delete: 'Delete records',
  activate: 'Activate records',
  deactivate: 'Deactivate records',
  import: 'Import records in bulk',
  export: 'Export records',
  approve: 'Approve records',
  reject: 'Reject records',
};

function capitalize(key: string): string {
  if (!key) return key;
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function unwrapData<T = unknown>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function normalizeNewShape(raw: unknown): PermissionsConfig | null {
  const obj = raw as { modules?: unknown; actionCatalog?: unknown } | null;
  if (!obj || !Array.isArray(obj.modules) || !Array.isArray(obj.actionCatalog)) return null;
  return {
    modules: obj.modules.map((m) => {
      const mm = m as Partial<PermissionsConfigModule>;
      return {
        key: String(mm.key ?? ''),
        name: String(mm.name ?? mm.key ?? ''),
        description: String(mm.description ?? ''),
        actions: Array.isArray(mm.actions) ? mm.actions.map(String) : [],
      };
    }),
    actionCatalog: obj.actionCatalog.map((a) => {
      const aa = a as Partial<PermissionsConfigAction>;
      return {
        key: String(aa.key ?? ''),
        name: String(aa.name ?? capitalize(String(aa.key ?? ''))),
        description: String(aa.description ?? DEFAULT_ACTION_DESCRIPTIONS[String(aa.key ?? '')] ?? ''),
      };
    }),
  };
}

function synthesizeFromLegacy(raw: unknown): PermissionsConfig {
  const obj = (raw ?? {}) as {
    modules?: Array<{ key?: string; name?: string; description?: string; actions?: string[] }>;
    actions?: Array<{ key?: string; name?: string; description?: string }>;
  };
  const legacyModules = Array.isArray(obj.modules) ? obj.modules : [];
  const legacyActions = Array.isArray(obj.actions) ? obj.actions : [];

  const modules: PermissionsConfigModule[] = legacyModules.map((m) => ({
    key: String(m.key ?? ''),
    name: String(m.name ?? m.key ?? ''),
    description: String(m.description ?? ''),
    actions: Array.isArray(m.actions) && m.actions.length
      ? m.actions.map(String)
      : legacyActions.map((a) => String(a.key ?? '')).filter(Boolean),
  }));

  const seen = new Set<string>();
  const actionCatalog: PermissionsConfigAction[] = [];
  for (const a of legacyActions) {
    const key = String(a.key ?? '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    actionCatalog.push({
      key,
      name: String(a.name ?? capitalize(key)),
      description: String(a.description ?? DEFAULT_ACTION_DESCRIPTIONS[key] ?? ''),
    });
  }
  for (const m of modules) {
    for (const k of m.actions) {
      if (seen.has(k)) continue;
      seen.add(k);
      actionCatalog.push({
        key: k,
        name: capitalize(k),
        description: DEFAULT_ACTION_DESCRIPTIONS[k] ?? '',
      });
    }
  }

  return { modules, actionCatalog };
}

export function usePermissionsConfig(department?: string) {
  return useQuery<PermissionsConfig>({
    queryKey: ['permissions-config', department ?? null],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const params = department ? { department } : {};
      try {
        const res = await api.get('/features/permissions-config', { params });
        const payload = unwrapData(res.data);
        const normalized = normalizeNewShape(payload);
        if (normalized) return normalized;
        return synthesizeFromLegacy(payload);
      } catch (err) {
        const status = (err as { code?: number; response?: { status?: number } })?.response?.status
          ?? (err as { code?: number })?.code;
        if (status !== 404) throw err;
        const legacy = await api.get('/roles/permissions-config', { params });
        return synthesizeFromLegacy(unwrapData(legacy.data));
      }
    },
  });
}

export default usePermissionsConfig;
